import { Section, useConfig } from '@seek/libs-shared';
import type { Country } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Autosuggest } from 'braid-design-system';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { SalariesTabModel } from '../../../pages/salaries/salariesTabModel';
import translations from './.vocab/index';

interface SalariesSearchSectionProps {
  model: Pick<SalariesTabModel, 'companyName'>;
}

interface AutosuggestValue {
  text: string;
  description?: string;
  value?: string;
}

// TODO: This data will provided by the model and transform into this format
// will get this updated when Graph Integration is done
const mockedRolesWithSalaryRange: AutosuggestValue[] = [
  { text: 'Data Scientist', value: 'data-scientist' },
  { text: 'Software Engineer', value: 'software-engineer' },
  { text: 'DevOps Engineer', value: 'devops-engineer' },
  { text: 'Product Manager', value: 'product-manager' },
  { text: 'Product Designer', value: 'product-designer' },
  { text: 'UX Researcher', value: 'ux-researcher' },
  { text: 'Frontend Developer', value: 'frontend-developer' },
  { text: 'Backend Developer', value: 'backend-developer' },
  { text: 'Full Stack Developer', value: 'full-stack-developer' },
  { text: 'Technical Lead', value: 'technical-lead' },
  { text: 'Engineering Manager', value: 'engineering-manager' },
  { text: 'QA Engineer', value: 'qa-engineer' },
  { text: 'Systems Engineer', value: 'systems-engineer' },
  { text: 'Cloud Engineer', value: 'cloud-engineer' },
];

const MINIMUM_ROLES_TO_SHOW = 6;

export const SalariesSearchSection = ({
  model,
}: SalariesSearchSectionProps) => {
  const { companyName } = model;
  const { t } = useTranslations(translations);
  const { country } = useConfig();
  const navigate = useNavigate();
  const params = useParams();

  const [role, setRole] = useState<AutosuggestValue>({
    text: '',
  });

  const countryNameMapper: Record<Country, string> = {
    au: t('Australia'),
    hk: t('Hong Kong'),
    id: t('Indonesia'),
    my: t('Malaysia'),
    nz: t('New Zealand'),
    ph: t('Philippines'),
    sg: t('Singapore'),
    th: t('Thailand'),
  };

  const isEnoughRoles =
    mockedRolesWithSalaryRange.length > MINIMUM_ROLES_TO_SHOW;

  if (isEnoughRoles) {
    return (
      <Section
        mode={'view'}
        heading={t('Company salaries', { companyName })}
        headingDescription={t('Explore how much...', {
          companyName,
          country: countryNameMapper[country],
        })}
        padding={'none'}
      >
        <Autosuggest
          id="roleFilter"
          placeholder={`e.g. ${mockedRolesWithSalaryRange[0].text}`}
          aria-label={'Search for job roles'}
          value={role}
          suggestions={mockedRolesWithSalaryRange}
          onChange={(e) => {
            setRole({ text: e.text });
            if (e.value && params.companySlug) {
              void navigate(
                `/companies/${params.companySlug}/salaries/${e.value}`,
              );
            }
          }}
          onClear={() => {
            setRole({ text: '' });
          }}
        />
      </Section>
    );
  } else {
    return null;
  }
};
